// Board management chrome for the UI-LAW §4c Board archetype: with no boards a
// single "Create dashboard" secondary Button, on a saved board the "Board actions"
// Menu (New… / Duplicate / Rename… / Delete… — danger last). The name forms
// live in trigger-anchored Popovers (new-board adds a start-from-template
// Select), the destructive confirm in the DashboardDeleteModal. Rendered ONLY
// for member+ callers (the container gates on `canAuthorDashboards`); every
// write stays server-authoritative and 403s surface as toasts.

import {
  useMemo,
  useRef,
  useState,
  type RefObject,
  type SyntheticEvent,
} from "react";

import type { components } from "../api/schema";
import { DASHBOARD_TEMPLATES } from "../lib/dashboardWidgets";
import { requestErrorDescription } from "../lib/errors";
import { type DashboardBoardsApi } from "../lib/useDashboardBoards";
import {
  Button,
  Inline,
  InlineError,
  Menu,
  Popover,
  Select,
  Stack,
  TextInput,
  type MenuItem,
} from "../ui";
import { DashboardDeleteModal } from "./DashboardDeleteModal";

type DashboardResponse = components["schemas"]["DashboardResponse"];

const BLANK_TEMPLATE_ID = "blank";

const ORG_SCOPE = "organization";
const AGENT_SCOPE = "agent";

export interface DashboardBoardManagerProps {
  /** The canonical sidebar agent scope — the default owner for new boards. */
  agentId?: string;
  boards: DashboardBoardsApi;
  selectedDashboard: DashboardResponse | undefined;
}

export function DashboardBoardManager({
  agentId,
  boards,
  selectedDashboard,
}: DashboardBoardManagerProps) {
  // The selected board and working scope are route/server state. Keying the
  // transient board controls to them clears only transient UI (open layers and
  // form drafts) without storing a second copy of either value.
  return (
    <DashboardBoardManagerContent
      key={`${agentId ?? "organization"}:${selectedDashboard?.id ?? "new"}`}
      agentId={agentId}
      boards={boards}
      selectedDashboard={selectedDashboard}
    />
  );
}

function DashboardBoardManagerContent({
  agentId,
  boards,
  selectedDashboard,
}: DashboardBoardManagerProps) {
  // Popover open-state (new/rename) and the delete Modal are transient
  // trigger-tied layers — component state, not URL.
  const [newName, setNewName] = useState("");
  const [newTemplateId, setNewTemplateId] = useState(
    DASHBOARD_TEMPLATES[0]?.id ?? BLANK_TEMPLATE_ID,
  );
  // New boards default to the sidebar model: agent selected -> that agent
  // owns the board; All agents -> organization-wide.
  const [newScope, setNewScope] = useState(agentId ? AGENT_SCOPE : ORG_SCOPE);
  const [renameName, setRenameName] = useState("");
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const newAnchorRef = useRef<HTMLButtonElement>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  // The Menu primitive owns its trigger Button and exposes no ref to it, so
  // the Popovers anchor through a resolver that finds the trigger inside the
  // wrapper — dismissal then returns focus to the actual Menu trigger.
  const menuTriggerRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current(): HTMLElement | null {
        return (
          menuWrapRef.current?.querySelector<HTMLElement>(
            'button[aria-haspopup="menu"]',
          ) ?? menuWrapRef.current
        );
      },
    }),
    [],
  );

  const submitNew = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    boards.createDashboard.mutate(
      {
        agentId: newScope === AGENT_SCOPE ? agentId : undefined,
        displayName: newName,
        templateId:
          newTemplateId === BLANK_TEMPLATE_ID ? undefined : newTemplateId,
      },
      {
        onSuccess: () => {
          setNewName("");
          setIsNewOpen(false);
        },
      },
    );
  };

  const submitRename = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    boards.renameDashboard.mutate(renameName, {
      onSuccess: () => {
        setIsRenameOpen(false);
      },
    });
  };

  const newForm = (
    <form onSubmit={submitNew}>
      <Stack gap={4}>
        <TextInput
          label="Name"
          required
          value={newName}
          onChange={(event) => {
            setNewName(event.currentTarget.value);
          }}
        />
        <Select
          label="Start from"
          value={newTemplateId}
          onChange={(event) => {
            setNewTemplateId(event.currentTarget.value);
          }}
        >
          {DASHBOARD_TEMPLATES.map((template) => (
            <option key={template.id} value={template.id}>
              {`${template.label} template — 11 starter widgets`}
            </option>
          ))}
          <option value={BLANK_TEMPLATE_ID}>Blank</option>
        </Select>
        {agentId ? (
          <Select
            label="Scope"
            value={newScope}
            onChange={(event) => {
              setNewScope(event.currentTarget.value);
            }}
          >
            <option value={AGENT_SCOPE}>
              This agent — listed under the selected agent
            </option>
            <option value={ORG_SCOPE}>
              Organization — shared across agents
            </option>
          </Select>
        ) : null}
        {boards.createDashboard.isError ? (
          <InlineError>
            {requestErrorDescription(
              boards.createDashboard.error,
              "Dashboard request failed.",
            )}
          </InlineError>
        ) : null}
        <Inline>
          <Button
            disabled={
              boards.createDashboard.isPending || newName.trim().length === 0
            }
            type="submit"
          >
            Create
          </Button>
          <Button
            disabled={boards.createDashboard.isPending}
            type="button"
            variant="secondary"
            onClick={() => {
              setIsNewOpen(false);
            }}
          >
            Cancel
          </Button>
        </Inline>
      </Stack>
    </form>
  );

  if (!selectedDashboard) {
    return (
      <div className="marker-popover-anchor">
        <Button
          aria-expanded={isNewOpen}
          aria-haspopup="dialog"
          ref={newAnchorRef}
          size="sm"
          variant="secondary"
          onClick={() => {
            setIsNewOpen((current) => !current);
          }}
        >
          Create dashboard
        </Button>
        <Popover
          align="end"
          anchorRef={newAnchorRef}
          aria-label="Create dashboard"
          isOpen={isNewOpen}
          onDismiss={() => {
            setIsNewOpen(false);
          }}
        >
          {newForm}
        </Popover>
      </div>
    );
  }

  const menuItems: MenuItem[] = [
    {
      icon: "plus",
      id: "new-dashboard",
      label: "Create dashboard…",
      onSelect: () => {
        setIsRenameOpen(false);
        setIsNewOpen(true);
      },
    },
    {
      icon: "copy",
      id: "duplicate-dashboard",
      label: "Duplicate",
      onSelect: () => {
        boards.duplicateDashboard.mutate(selectedDashboard);
      },
    },
    {
      icon: "pencil",
      id: "rename-dashboard",
      label: "Rename…",
      onSelect: () => {
        setRenameName(selectedDashboard.display_name);
        setIsNewOpen(false);
        setIsRenameOpen(true);
      },
    },
    // §4a Menu law: the danger item is last.
    {
      danger: true,
      icon: "trash",
      id: "delete-dashboard",
      label: "Delete…",
      onSelect: () => {
        setIsDeleteOpen(true);
      },
    },
  ];

  return (
    <>
      <div className="marker-popover-anchor" ref={menuWrapRef}>
        <Menu align="end" items={menuItems} label="Board actions" />
        <Popover
          align="end"
          anchorRef={menuTriggerRef}
          aria-label="Create dashboard"
          isOpen={isNewOpen}
          onDismiss={() => {
            setIsNewOpen(false);
          }}
        >
          {newForm}
        </Popover>
        <Popover
          align="end"
          anchorRef={menuTriggerRef}
          aria-label="Rename dashboard"
          isOpen={isRenameOpen}
          onDismiss={() => {
            setIsRenameOpen(false);
          }}
        >
          <form onSubmit={submitRename}>
            <Stack gap={4}>
              <TextInput
                label="Name"
                required
                value={renameName}
                onChange={(event) => {
                  setRenameName(event.currentTarget.value);
                }}
              />
              {boards.renameDashboard.isError ? (
                <InlineError>
                  {requestErrorDescription(
                    boards.renameDashboard.error,
                    "Dashboard request failed.",
                  )}
                </InlineError>
              ) : null}
              <Inline>
                <Button
                  disabled={
                    boards.renameDashboard.isPending ||
                    renameName.trim().length === 0
                  }
                  type="submit"
                >
                  Save name
                </Button>
                <Button
                  disabled={boards.renameDashboard.isPending}
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsRenameOpen(false);
                  }}
                >
                  Cancel
                </Button>
              </Inline>
            </Stack>
          </form>
        </Popover>
      </div>
      {isDeleteOpen ? (
        <DashboardDeleteModal
          dashboardName={selectedDashboard.display_name}
          error={
            boards.deleteDashboard.isError
              ? requestErrorDescription(
                  boards.deleteDashboard.error,
                  "Dashboard request failed.",
                )
              : null
          }
          isOpen
          isPending={boards.deleteDashboard.isPending}
          onCancel={() => {
            setIsDeleteOpen(false);
          }}
          onConfirm={() => {
            boards.deleteDashboard.mutate(undefined, {
              onSuccess: () => {
                setIsDeleteOpen(false);
              },
            });
          }}
        />
      ) : null}
    </>
  );
}
